import React from 'react';
import { FormattedMessage } from '@atlassian/jira-intl';
import messages from './messages';

export const getUsersAndRolesTasks = ({
	isOptimalTooling,
	isSiteAdmin,
	adminDifferenceCount,
	customRoleDifferenceCount,
	stakeholderCount,
}: {
	isOptimalTooling: boolean;
	isSiteAdmin: boolean;
	adminDifferenceCount: number;
	customRoleDifferenceCount: number;
	stakeholderCount: number;
}) => {
	const tasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.usersAndRolesTaskOneNonFinal} />,
		},
		{
			isCompleted: adminDifferenceCount === 0,
			description: (
				<FormattedMessage
					{...messages.usersAndRolesTaskTwoNonFinal}
					values={{
						count: adminDifferenceCount,
					}}
				/>
			),
		},
		{
			isCompleted: customRoleDifferenceCount === 0,
			description: (
				<FormattedMessage
					{...messages.usersAndRolesTaskThreeNonFinal}
					values={{
						count: customRoleDifferenceCount,
					}}
				/>
			),
		},
	];

	if (isOptimalTooling && isSiteAdmin && stakeholderCount > 0) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.usersAndRolesTaskFourNonFinal} />,
		});
	}

	return tasks;
};

export const getAlertsTasks = (isOptimalTooling?: boolean) => {
	const tasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.alertsTaskOneNonFinal} />,
		},
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.alertsTaskTwoNonFinal} />,
		},
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.alertsTaskThreeNonFinal} />,
		},
	];

	if (isOptimalTooling) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.alertsTaskFourNonFinal} />,
		});
	}

	return tasks;
};

const getCardTwoTasks = ({
	areOgAppsDeactivated,
	ogAppExistsAtTransitioningStart,
	isRBNEnabled,
}: {
	isJnaAppDownloaded: boolean;
	ogAppExistsAtTransitioningStart: boolean;
	isRBNEnabled: boolean;
	areOgRulesDuplicated: boolean;
	areOgAppsDeactivated: boolean;
}) => {
	// No second card
	if (!ogAppExistsAtTransitioningStart && !isRBNEnabled) {
		return [];
	}

	// 2D
	if (!ogAppExistsAtTransitioningStart && isRBNEnabled) {
		return [
			{
				isCompleted: true,
				description: <FormattedMessage {...messages.contactMethodTitleNonFinal} />,
			},
		];
	}

	// 2C
	if (ogAppExistsAtTransitioningStart && isRBNEnabled && areOgAppsDeactivated) {
		return [
			{
				isCompleted: true,
				description: <FormattedMessage {...messages.preventPushTitleNonFinal} />,
			},
			{
				isCompleted: true,
				description: <FormattedMessage {...messages.deleteOgContactMethodsTitleNonFinal} />,
			},
		];
	}

	// 2A & 2B & 2E
	return [
		{
			isCompleted: areOgAppsDeactivated,
			description: <FormattedMessage {...messages.notificationsTaskThreeNonFinal} />,
		},
	];
};

export const getNotificationsTasks = ({
	isJnaAppDownloaded,
	areOgAppsDeactivated,
	ogAppExistsAtTransitioningStart,
	isRBNEnabled,
	areOgRulesDuplicated,
}: {
	isJnaAppDownloaded: boolean;
	ogAppExistsAtTransitioningStart: boolean;
	isRBNEnabled: boolean;
	areOgRulesDuplicated: boolean;
	areOgAppsDeactivated: boolean;
}) => [
	{
		isCompleted: true,
		description: <FormattedMessage {...messages.notificationsTaskOneNonFinal} />,
	},
	{
		isCompleted: isJnaAppDownloaded,
		description: <FormattedMessage {...messages.notificationsTaskTwoNonFinal} />,
	},
	...getCardTwoTasks({
		isJnaAppDownloaded,
		areOgAppsDeactivated,
		ogAppExistsAtTransitioningStart,
		isRBNEnabled,
		areOgRulesDuplicated,
	}),
];

export const getTeamsTasks = (isOptimalTooling?: boolean, hasItem?: boolean) => {
	const tasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.teamsTaskOneNonFinal} />,
		},
	];

	if (isOptimalTooling && hasItem) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.teamsTaskTwoNonFinal} />,
		});
	}

	return tasks;
};

export const getChatToolsTasks = ({
	isChatToolsConnectedToJSM,
	isOgIntegrationsCopiedToJSM,
	isDuplicateNotificationsPrevented,
	isPrimaryAdmin,
	hasIntegration,
}: {
	isChatToolsConnectedToJSM: boolean;
	isOgIntegrationsCopiedToJSM: boolean;
	isDuplicateNotificationsPrevented: boolean;
	isPrimaryAdmin?: boolean;
	hasIntegration?: boolean;
}) => {
	const tasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.chatToolsTaskOneNonFinal} />,
		},
		{
			isCompleted: isDuplicateNotificationsPrevented,
			description: <FormattedMessage {...messages.chatToolsTaskFourNonFinal} />,
		},
	];

	const integrationTasks = [
		{
			isCompleted: isOgIntegrationsCopiedToJSM,
			description: <FormattedMessage {...messages.chatToolsTaskThreeNonFinal} />,
		},
	];

	const primaryAdminTasks = [
		{
			isCompleted: isChatToolsConnectedToJSM,
			description: <FormattedMessage {...messages.chatToolsTaskTwoNonFinal} />,
		},
	];

	if (hasIntegration) {
		tasks.splice(1, 0, ...integrationTasks);
	}

	if (isPrimaryAdmin) {
		tasks.splice(1, 0, ...primaryAdminTasks);
	}

	return tasks;
};

export const getAutomateAlertAndIncidentResponseTasks = () => [
	{
		isCompleted: true,
		description: (
			<FormattedMessage {...messages.automateAlertsAndIncidentResponseTaskOneNonFinal} />
		),
	},
	{
		isCompleted: true,
		description: (
			<FormattedMessage {...messages.automateAlertsAndIncidentResponseTaskTwoNonFinal} />
		),
	},
];

export const getIntegrationsTasks = ({
	hasSyncedIntegration,
	hasDiscontinuedIntegration,
	hasDeprecatedIntegration,
	hasIncomingCallIntegration,
	shouldHaveIncomingCallPermission,
}: {
	hasSyncedIntegration: boolean;
	hasDiscontinuedIntegration: boolean;
	hasDeprecatedIntegration: boolean;
	hasIncomingCallIntegration: boolean;
	shouldHaveIncomingCallPermission: boolean;
}) => {
	const integrationTasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.integrationsTaskOneNonFinal} />,
		},
	];
	if (hasSyncedIntegration) {
		integrationTasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.integrationsTaskTwoNonFinal} />,
		});
	}
	if (hasDiscontinuedIntegration) {
		integrationTasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.integrationsTaskThreeNonFinal} />,
		});
	}
	if (hasDeprecatedIntegration) {
		integrationTasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.integrationsTaskFourNonFinal} />,
		});
	}
	if (hasIncomingCallIntegration && shouldHaveIncomingCallPermission) {
		integrationTasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.integrationsTaskFiveNonFinal} />,
		});
	}
	return integrationTasks;
};

export const getDeprecatedOnesTasks = ({
	isAdmin,
	hasDeprecatedIntegration,
	hasDeprecatedGroups,
	hasDeprecatedGlobalHeartbeats,
	hasDeprecatedNotificationPolicies,
}: {
	isAdmin: boolean;
	hasDeprecatedIntegration: boolean;
	hasDeprecatedGroups: boolean;
	hasDeprecatedGlobalHeartbeats: boolean;
	hasDeprecatedNotificationPolicies: boolean;
}) => {
	// We have 7 static tasks which is displayed no matter what
	let totalCount = 7;

	// Deprecated integrations is dispplayed for admins and team admins
	if (hasDeprecatedIntegration) {
		totalCount += 1;
	}

	if (isAdmin) {
		if (hasDeprecatedGroups) {
			totalCount += 1;
		}
		if (hasDeprecatedGlobalHeartbeats) {
			totalCount += 1;
		}
		if (hasDeprecatedNotificationPolicies) {
			totalCount += 1;
		}
	}

	return [
		{
			isCompleted: true,
			description: (
				<FormattedMessage
					{...messages.deprecatedOnesTaskOneNonFinal}
					values={{
						deprecatedFeaturesCount: totalCount,
					}}
				/>
			),
		},
	];
};

export const getHeartbeatsTasks = ({
	hasDeprecatedTotalHeartbeats,
	hasDeprecatedGlobalHeartbeats,
	isExceedHeartbeatLimit,
}: {
	hasDeprecatedTotalHeartbeats: boolean;
	hasDeprecatedGlobalHeartbeats: boolean;
	isExceedHeartbeatLimit: boolean;
}) => {
	const tasks = [];
	if (hasDeprecatedTotalHeartbeats) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.heartbeatsTaskOneNonFinal} />,
		});
	}
	if (hasDeprecatedGlobalHeartbeats) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.heartbeatsTaskTwoNonFinal} />,
		});
	}
	if (isExceedHeartbeatLimit) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.heartbeatsTaskThreeNonFinal} />,
		});
	}
	return tasks;
};

export const getIncidentsTasks = ({
	isCopied,
	shouldDisplaySuccessBanner,
	isEligibleForCopy,
}: {
	isCopied: boolean;
	shouldDisplaySuccessBanner: boolean;
	isEligibleForCopy: boolean;
}) => {
	const tasks = [
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.incidentsTaskTwoNonFinal} />,
		},
		{
			isCompleted: true,
			description: <FormattedMessage {...messages.incidentsTaskThreeNonFinal} />,
		},
	];

	if (isCopied) {
		tasks.unshift({
			isCompleted: true,
			description: <FormattedMessage {...messages.incidentsTaskOneNonFinal} />,
		});
	}
	if (isEligibleForCopy) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.incidentsTaskFourNonFinal} />,
		});
	}
	if (shouldDisplaySuccessBanner) {
		tasks.push({
			isCompleted: true,
			description: <FormattedMessage {...messages.incidentsTaskFourSuccessNonFinal} />,
		});
	}

	return tasks;
};

export const getFindWhatYouAreLookingForTasks = () => [
	{
		isCompleted: true,
		description: <FormattedMessage {...messages.findWhatYouAreLookingForTaskOneNonFinal} />,
	},
];
